import { useEffect, useState, useCallback } from "react";
import axios from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import {
  Copy,
  ExternalLink,
  Plus,
  RefreshCw,
  Trash2,
  Pencil,
  Database,
  Eye,
} from "lucide-react";

const PLATFORMS = [
  { value: "google", label: "Google Merchant Center" },
  { value: "meta", label: "Meta Catalog" },
  { value: "tiktok", label: "TikTok Catalog" },
];

const STORE_PLATFORMS = [
  { value: "prestashop", label: "PrestaShop" },
  { value: "shopify", label: "Shopify" },
];

const DEFAULT_FORM = {
  name: "",
  platform: "google",
  store_platform: "prestashop",
  shop_id: "",
  shop_url: "",
  lang_id: "",
  lang_code: "",
  currency: "EUR",
  active: true,
  include_inactive: false,
  include_out_of_stock: true,
  include_combinations: false,
  require_gtin: false,
};

const platformBadgeColor = (p) => {
  if (p === "google") return "bg-blue-100 text-blue-800 border-blue-300";
  if (p === "meta") return "bg-indigo-100 text-indigo-800 border-indigo-300";
  if (p === "tiktok") return "bg-slate-900 text-white border-slate-900";
  return "bg-slate-100 text-slate-700";
};

export default function CatalogFeedsPage() {
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [discovery, setDiscovery] = useState({ shops: [], languages: [], currencies: ["EUR"] });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/catalog-feeds/configs`);
      setConfigs(res.data?.configs || []);
    } catch (e) {
      toast.error("No se pudieron cargar los feeds de catálogo");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDiscovery = useCallback(async (storePlatform) => {
    try {
      const res = await axios.get(`${API}/catalog-feeds/discover/${storePlatform}`);
      setDiscovery({
        shops: res.data?.shops || [],
        languages: res.data?.languages || [],
        currencies: res.data?.currencies || ["EUR"],
        shop_url_hint: res.data?.shop_url_hint,
      });
    } catch (e) {
      setDiscovery({ shops: [], languages: [], currencies: ["EUR"] });
    }
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  useEffect(() => {
    if (dialogOpen && form.store_platform) {
      loadDiscovery(form.store_platform);
    }
  }, [dialogOpen, form.store_platform, loadDiscovery]);

  const openCreate = () => {
    setEditingId(null);
    setForm(DEFAULT_FORM);
    setDialogOpen(true);
  };

  const openEdit = (cfg) => {
    setEditingId(cfg.config_id);
    setForm({
      name: cfg.name || "",
      platform: cfg.platform || "google",
      store_platform: cfg.store_platform || "prestashop",
      shop_id: cfg.shop_id || "",
      shop_url: cfg.shop_url || "",
      lang_id: cfg.lang_id || "",
      lang_code: cfg.lang_code || "",
      currency: cfg.currency || "EUR",
      active: cfg.active !== false,
      include_inactive: !!cfg.include_inactive,
      include_out_of_stock: cfg.include_out_of_stock !== false,
      include_combinations: !!cfg.include_combinations,
      require_gtin: !!cfg.require_gtin,
    });
    setDialogOpen(true);
  };

  const submitForm = async () => {
    try {
      if (!form.name?.trim()) {
        toast.error("Indica un nombre para el feed");
        return;
      }
      const payload = { ...form };
      if (editingId) {
        await axios.put(`${API}/catalog-feeds/configs/${editingId}`, payload);
        toast.success("Feed actualizado");
      } else {
        await axios.post(`${API}/catalog-feeds/configs`, payload);
        toast.success("Feed creado. Se generará en la próxima petición pública.");
      }
      setDialogOpen(false);
      await loadConfigs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar el feed");
    }
  };

  const removeConfig = async (cfg) => {
    if (!window.confirm(`¿Eliminar feed «${cfg.name}»?`)) return;
    try {
      await axios.delete(`${API}/catalog-feeds/configs/${cfg.config_id}`);
      toast.success("Feed eliminado");
      await loadConfigs();
    } catch (e) {
      toast.error("Error eliminando el feed");
    }
  };

  const regenerate = async (cfg) => {
    try {
      await axios.post(`${API}/catalog-feeds/configs/${cfg.config_id}/regenerate`);
      toast.success("Regenerando feed en segundo plano…");
      // Refresh after a short delay
      setTimeout(loadConfigs, 4000);
    } catch (e) {
      toast.error("Error iniciando regeneración");
    }
  };

  const openPreview = async (cfg) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData({ ...cfg, rows: [], headers: [] });
    try {
      const res = await axios.get(`${API}/catalog-feeds/configs/${cfg.config_id}/preview?limit=25`);
      setPreviewData({
        ...cfg,
        rows: res.data?.rows || [],
        headers: res.data?.headers || [],
        sync_hint: res.data?.sync_hint || null,
        incomplete_fields: res.data?.incomplete_fields || [],
      });
    } catch (e) {
      toast.error("No se pudo cargar la preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const publicUrl = (cfg) => {
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${base}/api/public/catalog-feed/${cfg.feed_token}/${cfg.platform}.csv`;
  };

  const copyUrl = async (cfg) => {
    try {
      await navigator.clipboard.writeText(publicUrl(cfg));
      toast.success("URL copiada");
    } catch (e) {
      toast.error("No se pudo copiar la URL");
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="catalog-feeds-page">
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Database className="w-7 h-7 text-emerald-600" />
              Feed Datos
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Catálogo completo para Google Merchant Center, Meta y TikTok.
              Multi-tienda + multi-idioma · sincronización diaria.
            </p>
          </div>
          <Button
            onClick={openCreate}
            data-testid="catalog-feed-create-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> Nuevo feed
          </Button>
        </header>

        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Cargando…</div>
          ) : configs.length === 0 ? (
            <div className="p-12 text-center">
              <Database className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 font-medium">
                Aún no tienes feeds de catálogo configurados
              </p>
              <p className="text-sm text-slate-500 mt-1">
                Crea uno por cada combinación tienda + idioma + plataforma destino.
              </p>
              <Button
                onClick={openCreate}
                className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="catalog-feed-empty-create-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Crear primer feed
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Plataforma</TableHead>
                  <TableHead>Tienda</TableHead>
                  <TableHead>Idioma</TableHead>
                  <TableHead>Productos</TableHead>
                  <TableHead>URL pública</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configs.map((cfg) => (
                  <TableRow key={cfg.config_id} data-testid={`catalog-feed-row-${cfg.config_id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <span>{cfg.name}</span>
                        {!cfg.active && (
                          <Badge variant="secondary" className="bg-slate-200 text-slate-700">
                            inactivo
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${platformBadgeColor(
                          cfg.platform
                        )}`}
                      >
                        {cfg.platform}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div>{cfg.store_platform}</div>
                      {cfg.shop_id && (
                        <div className="text-xs text-slate-500">id: {cfg.shop_id}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {cfg.lang_code || cfg.lang_id || "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {cfg.last_row_count != null ? cfg.last_row_count : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <code className="text-xs text-slate-600 truncate max-w-[200px]">
                          {publicUrl(cfg).slice(-40)}
                        </code>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => copyUrl(cfg)}
                          data-testid={`catalog-feed-copy-${cfg.config_id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <a
                          href={publicUrl(cfg)}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2 hover:bg-slate-100 rounded"
                          data-testid={`catalog-feed-open-${cfg.config_id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Previsualizar"
                          onClick={() => openPreview(cfg)}
                          data-testid={`catalog-feed-preview-${cfg.config_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Regenerar ahora"
                          onClick={() => regenerate(cfg)}
                          data-testid={`catalog-feed-regen-${cfg.config_id}`}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Editar"
                          onClick={() => openEdit(cfg)}
                          data-testid={`catalog-feed-edit-${cfg.config_id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Eliminar"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => removeConfig(cfg)}
                          data-testid={`catalog-feed-delete-${cfg.config_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Create / Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl" data-testid="catalog-feed-dialog">
            <DialogHeader>
              <DialogTitle>{editingId ? "Editar feed" : "Nuevo feed de catálogo"}</DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2">
                <Label>Nombre</Label>
                <Input
                  data-testid="catalog-feed-input-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej: ES Shopify Google"
                />
              </div>

              <div>
                <Label>Tienda origen</Label>
                <Select
                  value={form.store_platform}
                  onValueChange={(v) => setForm({ ...form, store_platform: v, shop_id: "", lang_id: "" })}
                >
                  <SelectTrigger data-testid="catalog-feed-select-store"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STORE_PLATFORMS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Plataforma destino</Label>
                <Select
                  value={form.platform}
                  onValueChange={(v) => setForm({ ...form, platform: v })}
                >
                  <SelectTrigger data-testid="catalog-feed-select-platform"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Tienda (id_shop / dominio)</Label>
                {discovery.shops?.length > 0 ? (
                  <Select
                    value={form.shop_id || ""}
                    onValueChange={(v) => setForm({ ...form, shop_id: v })}
                  >
                    <SelectTrigger data-testid="catalog-feed-select-shop">
                      <SelectValue placeholder="Selecciona tienda" />
                    </SelectTrigger>
                    <SelectContent>
                      {discovery.shops.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} {s.domain ? `(${s.domain})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    data-testid="catalog-feed-input-shop"
                    value={form.shop_id}
                    onChange={(e) => setForm({ ...form, shop_id: e.target.value })}
                    placeholder="Id de tienda o dominio"
                  />
                )}
              </div>

              <div>
                <Label>Idioma</Label>
                {discovery.languages?.length > 0 ? (
                  <Select
                    value={form.lang_id || ""}
                    onValueChange={(v) => {
                      const lang = discovery.languages.find((l) => l.id === v);
                      setForm({ ...form, lang_id: v, lang_code: lang?.iso_code || form.lang_code });
                    }}
                  >
                    <SelectTrigger data-testid="catalog-feed-select-lang">
                      <SelectValue placeholder="Selecciona idioma" />
                    </SelectTrigger>
                    <SelectContent>
                      {discovery.languages.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name} ({l.iso_code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    data-testid="catalog-feed-input-lang"
                    value={form.lang_id}
                    onChange={(e) => setForm({ ...form, lang_id: e.target.value })}
                    placeholder="lang_id (ej. 1)"
                  />
                )}
              </div>

              <div>
                <Label>URL de tienda</Label>
                <Input
                  data-testid="catalog-feed-input-shop-url"
                  value={form.shop_url}
                  onChange={(e) => setForm({ ...form, shop_url: e.target.value })}
                  placeholder={discovery.shop_url_hint || "https://www.midominio.com"}
                />
              </div>

              <div>
                <Label>Moneda</Label>
                <Select
                  value={form.currency}
                  onValueChange={(v) => setForm({ ...form, currency: v })}
                >
                  <SelectTrigger data-testid="catalog-feed-select-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(discovery.currencies || ["EUR"]).map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2 grid grid-cols-2 gap-3 pt-2">
                <label className="flex items-center justify-between rounded border border-slate-200 px-3 py-2">
                  <span className="text-sm">Feed activo</span>
                  <Switch
                    data-testid="catalog-feed-switch-active"
                    checked={form.active}
                    onCheckedChange={(v) => setForm({ ...form, active: v })}
                  />
                </label>
                <label className="flex items-center justify-between rounded border border-slate-200 px-3 py-2">
                  <span className="text-sm">Incluir inactivos</span>
                  <Switch
                    data-testid="catalog-feed-switch-inactive"
                    checked={form.include_inactive}
                    onCheckedChange={(v) => setForm({ ...form, include_inactive: v })}
                  />
                </label>
                <label className="flex items-center justify-between rounded border border-slate-200 px-3 py-2">
                  <span className="text-sm">Incluir sin stock</span>
                  <Switch
                    data-testid="catalog-feed-switch-oos"
                    checked={form.include_out_of_stock}
                    onCheckedChange={(v) => setForm({ ...form, include_out_of_stock: v })}
                  />
                </label>
                <label className="flex items-center justify-between rounded border border-slate-200 px-3 py-2">
                  <span className="text-sm">Requerir GTIN/EAN</span>
                  <Switch
                    data-testid="catalog-feed-switch-gtin"
                    checked={form.require_gtin}
                    onCheckedChange={(v) => setForm({ ...form, require_gtin: v })}
                  />
                </label>
                <label className="flex items-center justify-between rounded border border-slate-200 px-3 py-2">
                  <span className="text-sm">Incluir variantes (combinations)</span>
                  <Switch
                    data-testid="catalog-feed-switch-combinations"
                    checked={form.include_combinations}
                    onCheckedChange={(v) => setForm({ ...form, include_combinations: v })}
                  />
                </label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button
                onClick={submitForm}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="catalog-feed-submit-btn"
              >
                {editingId ? "Guardar cambios" : "Crear feed"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-6xl" data-testid="catalog-feed-preview-dialog">
            <DialogHeader>
              <DialogTitle>
                Preview — {previewData?.name} ({previewData?.platform})
              </DialogTitle>
            </DialogHeader>
            {previewData?.sync_hint && (
              <div className="bg-[#FEEEF0] border-l-4 border-[#C8192C] text-[#404040] text-[12.5px] px-4 py-3 rounded-r">
                <p className="font-semibold text-[#C8192C] mb-1">Aviso de sincronización</p>
                <p>{previewData.sync_hint}</p>
                {previewData?.incomplete_fields?.length > 0 && (
                  <p className="mt-1 text-[11.5px] text-[#888888]">
                    Campos vacíos: <code>{previewData.incomplete_fields.join(", ")}</code>
                  </p>
                )}
              </div>
            )}
            <div className="overflow-auto max-h-[60vh] border border-slate-200 rounded">
              {previewLoading ? (
                <div className="p-8 text-center text-slate-500">Cargando muestra…</div>
              ) : previewData?.rows?.length > 0 ? (
                <table className="text-xs w-full">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      {previewData.headers.map((h) => (
                        <th key={h} className="px-2 py-1 text-left font-medium text-slate-700 whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.rows.map((row, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        {previewData.headers.map((h) => (
                          <td key={h} className="px-2 py-1 align-top whitespace-nowrap max-w-[260px] truncate" title={row[h]}>
                            {row[h]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-8 text-center text-slate-500">
                  Sin productos que cumplan los filtros del feed.
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewOpen(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
